var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDiagnosticMessage, interpretTree, interpretTsl } from '../core/format';
import { parseTsl } from '../core/parser';
import { applySuggestion as applySuggestionText, suggest, valueSuggestion } from '../core/suggest';
var DEFAULT_PAGE_SIZE = 30;
var DEFAULT_DEBOUNCE_MS = 250;
/** Clamps a stored replace range to the current text length. */
var suggestionReplaceRange = function (range, text) { return ({
    start: Math.min(range.start, text.length),
    end: Math.min(range.end, text.length),
}); };
/**
 * Headless editor logic for a TSL query input: parsing, interpretation,
 * context-aware suggestions (fields/operators sync, values async and paged)
 * and keyboard handling. UI-framework agnostic — visual components live in
 * `@tritius/ui` (`TrTslQueryInput`).
 */
export var useTslEditor = function (options) {
    var registry = options.registry, value = options.value, onChange = options.onChange, onSubmit = options.onSubmit, fetchValues = options.fetchValues, _a = options.valuePageSize, valuePageSize = _a === void 0 ? DEFAULT_PAGE_SIZE : _a, _b = options.debounceMs, debounceMs = _b === void 0 ? DEFAULT_DEBOUNCE_MS : _b, transformSuggestions = options.transformSuggestions;
    var _c = useState(value.length), caret = _c[0], setCaret = _c[1];
    var _d = useState(false), isOpen = _d[0], setIsOpen = _d[1];
    var _e = useState(0), activeIndex = _e[0], setActiveIndex = _e[1];
    var _f = useState([]), valueItems = _f[0], setValueItems = _f[1];
    var _g = useState(false), isLoadingValues = _g[0], setIsLoadingValues = _g[1];
    var _h = useState(false), hasMoreValues = _h[0], setHasMoreValues = _h[1];
    var elementRef = useRef(null);
    var fetchStateRef = useRef({ requestId: 0, offset: 0 });
    var pendingCaretRef = useRef(null);
    var blurTimerRef = useRef(undefined);
    // Clear a pending blur-close timer on unmount so it cannot fire afterwards.
    useEffect(function () { return function () {
        if (blurTimerRef.current !== undefined) {
            clearTimeout(blurTimerRef.current);
        }
    }; }, []);
    var query = useMemo(function () { return parseTsl(value, registry); }, [value, registry]);
    var chips = useMemo(function () { return interpretTsl(query, registry); }, [query, registry]);
    var interpretation = useMemo(function () { return interpretTree(query, registry); }, [query, registry]);
    var translate = registry.translateFn;
    var diagnostics = useMemo(function () {
        return query.diagnostics.map(function (diagnostic) { return ({
            severity: diagnostic.severity,
            message: formatDiagnosticMessage(translate(diagnostic.messageKey), diagnostic.params),
            span: diagnostic.span,
        }); });
    }, [query, translate]);
    var suggestResult = useMemo(function () {
        var at = Math.min(caret, value.length);
        var result = suggest(value, at, registry);
        // CZNA-217
        return transformSuggestions ? transformSuggestions(result, { text: value, caret: at }) : result;
    }, [value, caret, registry, transformSuggestions]);
    var valueContext = fetchValues ? suggestResult.valueContext : undefined;
    var valueContextKey = valueContext
        ? "".concat(valueContext.fieldId, ":").concat(valueContext.prefix)
        : undefined;
    // Debounced, paged loading of value suggestions for the current context.
    useEffect(function () {
        // Invalidate any in-flight fetch up front — including when the value
        // context has just closed — so a late response from the previous context
        // cannot repopulate suggestions that no longer apply.
        var requestId = fetchStateRef.current.requestId + 1;
        fetchStateRef.current = { requestId: requestId, offset: 0 };
        setValueItems([]);
        setHasMoreValues(false);
        if (!valueContext || !fetchValues) {
            setIsLoadingValues(false);
            return undefined;
        }
        setIsLoadingValues(true);
        var timer = setTimeout(function () {
            fetchValues({
                fieldId: valueContext.fieldId,
                prefix: valueContext.prefix,
                offset: 0,
                limit: valuePageSize,
            })
                .then(function (page) {
                if (fetchStateRef.current.requestId !== requestId) {
                    return;
                }
                setValueItems(page.values.map(function (option) { return valueSuggestion(option.label, option.detail, registry); }));
                setHasMoreValues(page.hasMore);
                fetchStateRef.current.offset = page.values.length;
            })
                .catch(function () {
                // Suggestions are best-effort; a failed fetch just offers nothing.
            })
                .finally(function () {
                if (fetchStateRef.current.requestId === requestId) {
                    setIsLoadingValues(false);
                }
            });
        }, debounceMs);
        return function () {
            clearTimeout(timer);
            // Discard a fetch that already fired for this now-superseded context.
            fetchStateRef.current.requestId += 1;
        };
        // valueContextKey identifies the context; the object itself is recreated each parse.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueContextKey, fetchValues, valuePageSize, debounceMs, registry]);
    var loadMoreValues = useCallback(function () {
        if (!valueContext || !fetchValues || isLoadingValues || !hasMoreValues) {
            return;
        }
        var requestId = fetchStateRef.current.requestId;
        setIsLoadingValues(true);
        fetchValues({
            fieldId: valueContext.fieldId,
            prefix: valueContext.prefix,
            offset: fetchStateRef.current.offset,
            limit: valuePageSize,
        })
            .then(function (page) {
            if (fetchStateRef.current.requestId !== requestId) {
                return;
            }
            setValueItems(function (previous) { return __spreadArray(__spreadArray([], previous, true), page.values.map(function (option) { return valueSuggestion(option.label, option.detail, registry); }), true); });
            setHasMoreValues(page.hasMore);
            fetchStateRef.current.offset += page.values.length;
        })
            // CZNA-217: annotated — the module build has noImplicitAny on.
            .catch(function () { return undefined; })
            .finally(function () {
            if (fetchStateRef.current.requestId === requestId) {
                setIsLoadingValues(false);
            }
        });
    }, [valueContext, fetchValues, isLoadingValues, hasMoreValues, valuePageSize, registry]);
    var suggestions = useMemo(function () { return __spreadArray(__spreadArray([], suggestResult.items, true), valueItems, true); }, [suggestResult, valueItems]);
    useEffect(function () {
        setActiveIndex(0);
    }, [suggestions.length, valueContextKey]);
    // Restore the caret position after a programmatic completion.
    useEffect(function () {
        var pending = pendingCaretRef.current;
        if (pending !== null && elementRef.current) {
            elementRef.current.setSelectionRange(pending, pending);
            pendingCaretRef.current = null;
        }
    }, [value]);
    var applyAt = useCallback(function (index) {
        var suggestion = suggestions[index];
        if (!suggestion) {
            return;
        }
        var applied = applySuggestionText(value, suggestionReplaceRange(suggestResult.replaceRange, value), suggestion.insertText, suggestion.caretOffset);
        pendingCaretRef.current = applied.caret;
        setCaret(applied.caret);
        onChange(applied.text);
    }, [suggestions, suggestResult, value, onChange]);
    var close = useCallback(function () { return setIsOpen(false); }, []);
    return {
        query: query,
        chips: chips,
        interpretation: interpretation,
        diagnostics: diagnostics,
        suggestions: suggestions,
        activeIndex: activeIndex,
        isOpen: isOpen && suggestions.length > 0,
        isLoadingValues: isLoadingValues,
        hasMoreValues: hasMoreValues,
        loadMoreValues: loadMoreValues,
        applySuggestion: applyAt,
        close: close,
        setInputElement: function (element) {
            elementRef.current = element;
        },
        inputProps: {
            value: value,
            onChange: function (event) {
                var _a;
                setIsOpen(true);
                setCaret((_a = event.currentTarget.selectionStart) !== null && _a !== void 0 ? _a : event.currentTarget.value.length);
                onChange(event.currentTarget.value);
            },
            onSelect: function (event) {
                var _a;
                setCaret((_a = event.currentTarget.selectionStart) !== null && _a !== void 0 ? _a : value.length);
            },
            onKeyDown: function (event) {
                var open = isOpen && suggestions.length > 0;
                if (event.key === 'ArrowDown' && open) {
                    event.preventDefault();
                    setActiveIndex(function (previous) { return (previous + 1) % suggestions.length; });
                    return;
                }
                if (event.key === 'ArrowUp' && open) {
                    event.preventDefault();
                    setActiveIndex(function (previous) { return (previous - 1 + suggestions.length) % suggestions.length; });
                    return;
                }
                if ((event.key === 'Tab' || event.key === 'Enter') && open) {
                    event.preventDefault();
                    applyAt(activeIndex);
                    return;
                }
                if (event.key === 'Enter') {
                    event.preventDefault();
                    onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(query);
                    return;
                }
                if (event.key === 'Escape') {
                    setIsOpen(false);
                }
            },
            onFocus: function () { return setIsOpen(true); },
            onBlur: function () {
                // Delay so a click on a suggestion lands before the dropdown closes;
                // tracked in a ref so it can be cancelled on unmount.
                if (blurTimerRef.current !== undefined) {
                    clearTimeout(blurTimerRef.current);
                }
                blurTimerRef.current = setTimeout(function () { return setIsOpen(false); }, 150);
            },
        },
    };
};
